import {
  init,
  initData,
  swipeBehavior,
  viewport,
  mockTelegramEnv,
  themeParamsState,
  emitEvent,
  isViewportStable,
  isViewportExpanded,
  viewportWidth,
  viewportHeight,
  requestSafeAreaInsets,
  requestContentSafeAreaInsets,
} from "@telegram-apps/sdk-react";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants.ts";
import { captureTelegramError } from "utils/sentry.ts";
import {
  ACCESS_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
} from "@shared/common/constants.ts";
import { retrieveRawInitData } from "@telegram-apps/sdk";
import {
  safeRetrieveLaunchParams,
  parseInitDataHash,
  getCleanOldInitDataHashes,
} from "features/telegram/utils.ts";

export function initTelegramWebApp() {
  if (IS_TELEGRAM_WEB_APP) {
    const launchParams = safeRetrieveLaunchParams();
    let currentInitData: string | undefined;
    try {
      currentInitData = retrieveRawInitData();
    } catch {}

    const currentHash = currentInitData
      ? parseInitDataHash(currentInitData)
      : undefined;
    const savedHashes = getCleanOldInitDataHashes();
    const isNew = currentHash ? !(currentHash in savedHashes) : false;

    if (currentHash && isNew) {
      localStorage?.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      localStorage?.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    }

    if (!launchParams) {
      // If launchParams retrieval failed, we might still want to proceed with init()
      // but some logic below depends on launchParams
    }
    const platform = launchParams?.tgWebAppPlatform;
    const mockForWebK = platform === "web";
    const mockForMacOS = platform === "macos";
    const noInsets = { left: 0, top: 0, right: 0, bottom: 0 };
    if (mockForWebK || mockForMacOS) {
      try {
        mockTelegramEnv({
          onEvent(event, next) {
            if (mockForMacOS) {
              if (event[0] === "web_app_request_theme") {
                return emitEvent("theme_changed", {
                  theme_params: themeParamsState(),
                });
              }
              if (event[0] === "web_app_request_viewport") {
                return emitEvent("viewport_changed", {
                  height: viewportHeight(),
                  width: viewportWidth(),
                  is_expanded: isViewportExpanded(),
                  is_state_stable: isViewportStable(),
                });
              }
            }
            if (mockForWebK && event[0] === "web_app_request_safe_area") {
              return emitEvent("safe_area_changed", noInsets);
            }
            if (event[0] === "web_app_request_content_safe_area") {
              return emitEvent("content_safe_area_changed", noInsets);
            }
            next();
          },
        });
      } catch (error) {
        captureTelegramError(error, {
          method: "mockTelegramEnv",
          location: "initTelegramWebApp - mocking environment",
          additionalData: { platform },
        });
      }
    }

    try {
      init();
    } catch (error) {
      captureTelegramError(error, {
        method: "init",
        location: "initTelegramWebApp - SDK initialization",
        additionalData: { launchParams },
      });
    }

    try {
      initData.restore();
    } catch (error) {
      captureTelegramError(error, {
        method: "initData.restore",
        location: "initTelegramWebApp - restoring init data",
        additionalData: { launchParams },
      });
    }

    try {
      if (viewport.mount.isAvailable()) {
        viewport.mount().catch((error) => {
          captureTelegramError(error, {
            method: "viewport.mount - promise rejection",
            location: "initTelegramWebApp - mounting viewport",
            additionalData: { launchParams },
          });
        });
      }
    } catch (error) {
      captureTelegramError(error, {
        method: "viewport.mount",
        location: "initTelegramWebApp - mounting viewport",
        additionalData: { launchParams },
      });
    }

    try {
      if (swipeBehavior.mount.isAvailable()) {
        swipeBehavior.mount();
      }
    } catch (error) {
      captureTelegramError(error, {
        method: "swipeBehavior.mount",
        location: "initTelegramWebApp - mounting swipe behavior",
        additionalData: { launchParams },
      });
    }

    // Request safe area insets explicitly to ensure we receive initial values and updates
    try {
      if (requestSafeAreaInsets.isAvailable()) {
        requestSafeAreaInsets()?.catch((error) => {
          captureTelegramError(error, {
            method: "requestSafeAreaInsets - promise rejection",
            location: "initTelegramWebApp - requesting safe area insets",
            additionalData: { launchParams },
          });
        });
      }
    } catch (error) {
      captureTelegramError(error, {
        method: "requestSafeAreaInsets",
        location: "initTelegramWebApp - requesting safe area insets",
        additionalData: { launchParams },
      });
    }

    try {
      if (requestContentSafeAreaInsets.isAvailable()) {
        requestContentSafeAreaInsets()?.catch((error) => {
          captureTelegramError(error, {
            method: "requestContentSafeAreaInsets - promise rejection",
            location:
              "initTelegramWebApp - requesting content safe area insets",
            additionalData: { launchParams },
          });
        });
      }
    } catch (error) {
      captureTelegramError(error, {
        method: "requestContentSafeAreaInsets",
        location: "initTelegramWebApp - requesting content safe area insets",
        additionalData: { launchParams },
      });
    }
  }
}
